import { useState } from "react";
import { paymentRequestCreate } from "@shared/schemas/payments";
import { formatCents } from "@shared/money";
import { damageCollected } from "@shared/paymentAmounts";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { useFormValidation } from "@web/lib/formValidation";
import { todayIn } from "@web/lib/format";
import { newId } from "@shared/ids";
import type { BookingDetail } from "@web/lib/queries";
import { paymentApi } from "@web/lib/queries-payments";
import { useMe } from "@web/lib/session";
import { BottomSheet, Button, MoneyField, Picker, Stack, Text, TextField, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

const KINDS = [
  { value: "deposit", label: "Deposit" },
  { value: "balance", label: "Balance" },
  { value: "damage_deposit", label: "Damage deposit" },
  { value: "extra", label: "Extra" },
  { value: "custom", label: "Custom" },
];

/** Ask the client for money (SPEC §7.6). The amount entered is a ceiling: the
 * pay page charges whatever that kind still owes when the client opens it, so a
 * request written today cannot overcharge after a part payment lands. */
export function RequestPaymentSheet({ b, open, onClose }: { b: BookingDetail; open: boolean; onClose: () => void }) {
  const me = useMe();
  const toast = useToast();
  const depositOwed = Math.max(0, b.depositAmount - b.paidTotal);
  const [kind, setKind] = useState(depositOwed > 0 ? "deposit" : "balance");
  const [amount, setAmount] = useState<number | null>(depositOwed > 0 ? depositOwed : Math.max(0, b.balanceDue) || null);
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(kind === "deposit" ? b.depositDueDate ?? todayIn(me.organization.timezone) : b.balanceDueDate ?? "");
  // One identity per opened sheet, so a double submit returns the same request.
  const [clientRequestId] = useState(() => newId());
  const body = { kind, amount, description: description || null, dueDate: dueDate || null, clientRequestId };
  const [initialDraft] = useState(() => JSON.stringify(body));
  const parsed = parseInput(paymentRequestCreate, body, { type: "payment_request" }, "payment_request.create");
  const validation = useFormValidation(evaluation(parsed.issues, false));
  const { busy, field } = validation;

  function pickKind(next: string) {
    setKind(next);
    if (next === "deposit") { setAmount(depositOwed || null); setDueDate(b.depositDueDate ?? ""); }
    else if (next === "balance") { setAmount(Math.max(0, b.balanceDue) || null); setDueDate(b.balanceDueDate ?? ""); }
    else if (next === "damage_deposit") { setAmount(Math.max(0, b.damageAmount - damageCollected(b.payments)) || null); setDueDate(b.balanceDueDate ?? ""); }
  }

  async function save() {
    if (!parsed.success || !validation.begin("payment_request.create")) return;
    try {
      await paymentApi.request(b.id, parsed.data);
      toast.success(`Requested ${formatCents(parsed.data.amount)}`);
      onClose();
    } catch (error) {
      validation.reject(error);
      toast.error(errorMessage(error));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title="Request payment"
      footer={<Button loading={busy} onClick={save}>Create request</Button>}
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={!busy && JSON.stringify(body) !== initialDraft} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <Picker label="For" value={kind} onChange={value => validation.edit(() => pickKind(value))} options={KINDS} {...field("kind")} />
        <MoneyField label="Amount" value={amount} onChange={value => validation.edit(() => setAmount(value))} {...field("amount")} />
        <Text variant="caption" tone="secondary">
          This is the most the client can be charged. The pay page charges whatever this still owes when they open it.
        </Text>
        <TextField label="Description (optional)" value={description} onChange={event => validation.edit(() => setDescription(event.target.value))} {...field("description")} />
        <TextField label="Due date" type="date" value={dueDate} onChange={event => validation.edit(() => setDueDate(event.target.value))} {...field("dueDate")} />
      </Stack>
    </BottomSheet>
  );
}
